import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const SLOTS_ORDER = [
  'arrival', 'breakfast', 'morning1', 'morning2', 'lunch',
  'afternoon1', 'afternoon2', 'evening1', 'evening2', 'evmeal', 'departure',
];
const SLOT_LABELS = {
  arrival: 'Arrival Flight', breakfast: 'Breakfast', morning1: 'Morning',
  morning2: 'Morning', lunch: 'Lunch', afternoon1: 'Afternoon',
  afternoon2: 'Afternoon', evening1: 'Evening', evening2: 'Evening',
  evmeal: 'Evening Meal', departure: 'Departure Flight',
};

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}
function numDays(dep, ret) {
  if (!dep || !ret) return 0;
  return Math.max(1, Math.round((new Date(ret) - new Date(dep)) / 86400000) + 1);
}
function dayLabel(dep, idx) {
  if (!dep) return `Day ${idx + 1}`;
  const d = new Date(dep);
  d.setDate(d.getDate() + idx);
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

export default function SkeletalInternalApprovalPage() {
  const [params] = useSearchParams();
  const token     = params.get('token');
  const urlAction = params.get('action'); // 'approve' | 'decline' | 'amend'

  const [state,    setState]    = useState('loading');
  const [tokenDoc, setTokenDoc] = useState(null);
  const [tour,     setTour]     = useState(null);
  const [grid,     setGrid]     = useState({});
  const [action,   setAction]   = useState(null);
  const [comment,  setComment]  = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) { setState('error'); setErrorMsg('No approval token found in this link.'); return; }
    (async () => {
      const tokenSnap = await getDoc(doc(db, 'skeletalInternalApprovalTokens', token));
      if (!tokenSnap.exists()) { setState('error'); setErrorMsg('This link is invalid or has expired.'); return; }
      const td = tokenSnap.data();
      if (td.used) { setState('already_used'); setTokenDoc(td); return; }
      setTokenDoc(td);
      const [tourSnap, metaSnap] = await Promise.all([
        getDoc(doc(db, 'requests', td.requestId)),
        getDoc(doc(db, 'requests', td.requestId, 'itineraryMeta', 'skeletal')),
      ]);
      setTour(tourSnap.exists() ? tourSnap.data() : {});
      setGrid(metaSnap.exists() ? (metaSnap.data().grid || {}) : {});
      if (urlAction === 'approve' || urlAction === 'decline' || urlAction === 'amend') setAction(urlAction);
      setState('ready');
    })().catch((err) => { console.error(err); setState('error'); setErrorMsg('Failed to load. Please try again.'); });
  }, [token, urlAction]);

  async function handleSubmit() {
    if (!action) return;
    if (action === 'amend' && !comment.trim()) return;
    setState('submitting');
    try {
      await updateDoc(doc(db, 'skeletalInternalApprovalTokens', token), {
        action, comment: comment.trim() || null, used: true, respondedAt: serverTimestamp(),
      });
      setState('done');
    } catch (err) {
      console.error(err);
      setState('error');
      setErrorMsg('Failed to submit your response. Please try again.');
    }
  }

  const nDays    = tour ? numDays(tour.departureDate, tour.returnDate) : 0;
  const tourName = tour ? (tour.tourName || `${tour.sport} — ${tour.destination}`) : '';

  if (state === 'loading') return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <p className="text-[#9CA3AF] text-[14px]">Loading…</p>
    </div>
  );

  if (state === 'error') return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
      <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
        <p className="text-[15px] font-semibold text-[#111827] mb-2">Link not valid</p>
        <p className="text-[13px] text-[#6B7280]">{errorMsg}</p>
      </div>
    </div>
  );

  if (state === 'already_used') {
    const label = tokenDoc?.action === 'approve' ? 'Approved' : tokenDoc?.action === 'decline' ? 'Declined' : 'Amendments requested';
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
          <p className="text-[15px] font-semibold text-[#111827] mb-2">Response already recorded</p>
          <p className="text-[13px] text-[#6B7280]">You have already submitted: <strong>{label}</strong>.</p>
          {tokenDoc?.comment && <p className="text-[12px] text-[#6B7280] mt-2 italic">"{tokenDoc.comment}"</p>}
        </div>
      </div>
    );
  }

  if (state === 'done') {
    const messages = {
      approve: { title: 'Itinerary approved', body: 'The approval request has been sent to the coach / tour organiser.' },
      decline: { title: 'Itinerary declined', body: 'The team has been notified and will revise the itinerary.' },
      amend:   { title: 'Amendment request sent', body: 'Your comments have been added to the itinerary in the portal and the team has been notified.' },
    };
    const msg = messages[action] || messages.amend;
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-[#1A7A4A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-[17px] font-bold text-[#111827] mb-2">{msg.title}</p>
          <p className="text-[13px] text-[#6B7280]">{msg.body}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="bg-[#0D2240] rounded-[12px] p-6 text-center">
          <p className="text-white font-bold text-[18px]">MST Sports Tours — Internal Review</p>
          <p className="text-[#93C5FD] text-[13px] mt-1">Skeletal Itinerary Approval</p>
        </div>

        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
          <p className="text-[15px] font-bold text-[#111827] mb-1">{tourName}</p>
          <p className="text-[13px] text-[#6B7280]">
            {[formatDate(tour?.departureDate), formatDate(tour?.returnDate)].filter(Boolean).join(' – ')}
            {tour?.participants ? ` · ${tour.participants} participants` : ''}
          </p>
          <p className="text-[12px] text-[#9CA3AF] mt-2">
            Please review the skeletal itinerary below and approve, decline, or request amendments.
          </p>
        </div>

        {nDays > 0 && (
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-4">Itinerary Overview</p>
            <div className="space-y-4">
              {Array.from({ length: nDays }, (_, d) => {
                const daySlots = SLOTS_ORDER
                  .map((key) => ({ key, cell: grid[`${d}_${key}`] }))
                  .filter(({ cell }) => cell?.label);
                if (!daySlots.length) return null;
                return (
                  <div key={d}>
                    <p className="text-[12px] font-bold text-[#374151] mb-2">{dayLabel(tour?.departureDate, d)}</p>
                    <div className="space-y-1.5 pl-3 border-l-2 border-[#E5E7EB]">
                      {daySlots.map(({ key, cell }) => (
                        <div key={key} className="flex items-center gap-2">
                          <span className={`text-[10px] font-semibold uppercase tracking-[0.7px] w-20 shrink-0 ${
                            cell.type === 'flight' ? 'text-[#16A34A]' : cell.type === 'meal' ? 'text-[#D97706]' : 'text-[#6B7280]'
                          }`}>{SLOT_LABELS[key] || key}</span>
                          <span className="text-[13px] text-[#111827]">{cell.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
          <p className="text-[13px] font-semibold text-[#374151] mb-4">Your decision</p>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              { id: 'approve', label: '✓ Approve',          cls: action === 'approve' ? 'border-[#16A34A] bg-[#F0FDF4] text-[#15803D]' : 'border-[#E5E7EB] text-[#374151] hover:border-[#16A34A]' },
              { id: 'decline', label: '✗ Decline',          cls: action === 'decline' ? 'border-[#DC2626] bg-[#FEF2F2] text-[#DC2626]' : 'border-[#E5E7EB] text-[#374151] hover:border-[#DC2626]' },
              { id: 'amend',   label: '✏ Request amendments', cls: action === 'amend'   ? 'border-[#D97706] bg-[#FFFBEB] text-[#D97706]' : 'border-[#E5E7EB] text-[#374151] hover:border-[#D97706]' },
            ].map(({ id, label, cls }) => (
              <button key={id} type="button" onClick={() => setAction(id)}
                className={`border-2 rounded-[10px] px-3 py-3 text-[13px] font-semibold transition text-center ${cls}`}>
                {label}
              </button>
            ))}
          </div>

          {(action === 'amend' || action === 'decline') && (
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#374151] mb-1">
                {action === 'amend' ? <>Comments / required changes <span className="text-red-500">*</span></> : 'Reason (optional)'}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                placeholder={action === 'amend' ? 'e.g. Please move the training session to Day 2 morning and add a city tour on Day 3…' : 'Let us know why…'}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] resize-none"
              />
              {action === 'amend' && <p className="text-[11px] text-[#9CA3AF] mt-1">Your comments will appear on the itinerary page in the portal.</p>}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!action || (action === 'amend' && !comment.trim()) || state === 'submitting'}
            className="w-full py-3 bg-[#0D2240] hover:bg-[#1A4A8A] text-white font-semibold text-[14px] rounded-[10px] transition disabled:opacity-50"
          >
            {state === 'submitting' ? 'Submitting…' : 'Submit decision'}
          </button>
        </div>
      </div>
    </div>
  );
}
